import { createUrlResolver } from '@seek/melways-sites';
import {
  ButtonLink as BraidButton,
  Link as BraidLink,
  TextLink as BraidTextLink,
} from 'braid-design-system';
import type { FunctionComponent } from 'react';
import { matchRoutes, useHref } from 'react-router';

import { routes } from 'src/routes';
import { useLanguage, useSite } from 'src/utils/hooks/useContext';
import { useQuery } from 'src/utils/hooks/useQuery';
import { useTracking } from 'src/utils/hooks/useTracking';

type LinkProductType = 'candidate' | 'employer';

interface LinkProps {
  tracking: string;
  component?: typeof BraidLink | typeof BraidButton;
  href: string;
  product?: LinkProductType;
  children: React.ReactNode;
  target?: '_self' | '_blank' | '_parent' | '_top';
  skipResolvedUrl?: boolean; // Components like AppHeader and Footer from package have already resolved their link with language
}

export const Link: FunctionComponent<LinkProps> = ({
  tracking,
  children,
  href,
  product = 'employer',
  skipResolvedUrl = false,

  component: Component = BraidLink,
  ...restProps
}) => {
  const version = useQuery().get('v');
  const language = useLanguage() || 'en-AU'; // for testing purpose in pipeline
  const site = useSite() || 'employer-seek-au'; // for testing puprose in pipeline
  const { trackEvent } = useTracking();

  const isInternal = matchRoutes(routes, href);
  const internalLink = useHref(version ? `${href}?v=${version}` : href);

  /* Supplying skipResolvedUrl skips the UrlResolver and creates an external or non-seek link. */
  const resolveHref = () => {
    if (isInternal !== null) {
      return internalLink;
    }

    if (skipResolvedUrl) {
      return href;
    }
    return createUrlResolver({
      site,
      language,
    })({
      language,
      product,
      path: href,
    });
  };

  const onClick = () => {
    trackEvent(tracking);
  };
  const combinedProps = {
    onClick,
    href: resolveHref(),
    ...restProps,
  };
  return <Component {...combinedProps}>{children}</Component>;
};

type ButtonProps = Omit<LinkProps, 'component'> &
  React.ComponentProps<typeof BraidButton>;

export const ButtonLink = (props: ButtonProps) => (
  <Link component={BraidButton} {...props} />
);

export type TextLinkProps = Omit<LinkProps, 'component'> &
  React.ComponentProps<typeof BraidTextLink>;

export const TextLink = (props: TextLinkProps) => (
  <Link component={BraidTextLink} {...props} />
);
